import * as React from 'react';
import { TriangleAlert } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Card with a consistent icon + title header used by all stat panels. */
export function PanelCard({
  icon,
  title,
  action,
  className,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn('gap-0 overflow-hidden py-0', className)}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 border-b px-5 py-4 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2 text-sm font-medium tracking-wide text-muted-foreground uppercase">
          {icon}
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className="px-5 py-5">{children}</CardContent>
    </Card>
  );
}

/** Label / value row used inside stat cards. */
export function StatRow({
  label,
  value,
  sub,
  className,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-4', className)}>
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right">
        <span className="font-mono text-sm font-medium">{value}</span>
        {sub !== undefined && (
          <span className="ml-1.5 text-xs text-muted-foreground">{sub}</span>
        )}
      </span>
    </div>
  );
}

/** Inline error shown inside a card when its query failed permanently. */
export function ErrorNotice({ message }: { message?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-xs text-red-600 dark:text-red-400">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
      <span>{message ?? 'Failed to load data. The API may be rate-limited — retrying automatically with backoff.'}</span>
    </div>
  );
}

export function SkeletonLines({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton
          key={i}
          className="h-4"
          style={{ width: `${100 - i * 12}%` }}
        />
      ))}
    </div>
  );
}
